import type { ApiClient } from './ApiClient';
import {
  postAllowingDuplicate,
  type CapacityWarning,
  type DuplicateResolution,
  type ExistingStudentMatch,
  type Sex,
  type Student,
} from './student';

// Formes alignées sur apps/api/src/routes/enrollments.ts (ENR-01).

export interface EnrollableSchool {
  id: string;
  name: string;
  addressRegion: string;
  addressDepartment: string;
  addressCommune: string;
}

export interface SearchEnrollableSchoolsResponse {
  schools: EnrollableSchool[];
}

export function searchEnrollableSchools(client: ApiClient, q: string, accessToken: string) {
  return client.get<SearchEnrollableSchoolsResponse>('/schools/directory', { query: { q }, accessToken });
}

export interface SchoolEnrollmentInfo {
  school: EnrollableSchool;
  activeSchoolYear: { id: string; label: string } | null;
  availableLevels: string[];
  openPhase: { id: string; label: string } | null;
}

export function getSchoolEnrollmentInfo(client: ApiClient, schoolId: string, accessToken: string) {
  return client.get<SchoolEnrollmentInfo>(`/schools/directory/${schoolId}/enrollment-info`, { accessToken });
}

export interface CreateEnrollmentPayload {
  declaredChildId: string;
  tenantId: string;
  level: string;
  // PAR-13 : optionnel SI le `DeclaredChild` référencé porte déjà un sexe connu
  // (déclaré via `POST /parents/children`, désormais requis à la déclaration) —
  // sinon reste requis comme avant ce ticket. La valeur connue prime TOUJOURS côté
  // serveur, voir apps/api/src/routes/enrollments.ts.
  sex?: 'M' | 'F';
  // Correctif "déjà inscrit ailleurs" (ENR-01, point 2) — même pattern que
  // `confirmDuplicate` de PAR-02 : `true` UNIQUEMENT pour un renvoi explicite après
  // confirmation du parent.
  confirmDuplicate?: boolean;
  // ENR-20 : flag DÉDIÉ, distinct de `confirmDuplicate` ci-dessus — répond à un
  // AXE DIFFÉRENT de doublon (nom+prénom+date de naissance DANS LE MÊME tenant,
  // sous un AUTRE `declaredChildId`, voir `EnrollmentDuplicateInTenant`
  // ci-dessous). `true` UNIQUEMENT pour un renvoi explicite après confirmation.
  confirmDuplicateInTenant?: boolean;
}

export interface CreateEnrollmentResponse {
  student: { id: string; firstName: string; lastName: string };
  enrollment: { id: string; status: string; requestedLevel: string; schoolYear: string };
}

export function createEnrollment(client: ApiClient, payload: CreateEnrollmentPayload, accessToken: string) {
  return client.post<CreateEnrollmentResponse>('/enrollments', payload, { accessToken });
}

// Corps de l'erreur 409 renvoyée par POST /enrollments quand l'enfant est déjà
// inscrit ailleurs et que `confirmDuplicate` n'a pas été envoyé à `true` — voir
// `ApiError.body` (ApiClient.ts) ; un `catch` doit vérifier `error ===
// 'enrollment_confirmation_required'` avant de la lire.
export interface EnrollmentConfirmationRequired {
  error: 'enrollment_confirmation_required';
  message: string;
  existingSchool: EnrollableSchool | null;
}

// ENR-20 : corps de l'erreur 409 renvoyée par POST /enrollments quand un Student
// existe déjà DANS LE MÊME tenant avec le même nom+prénom+date de naissance, sous
// un AUTRE `declaredChildId` (fratrie jumelle légitime, doublon de déclaration, ou
// élève déjà créé côté école via SCH-06/SCH-18) — un `catch` doit vérifier
// `error === 'duplicate_student_in_tenant'` avant de la lire. AXE DIFFÉRENT
// d'`EnrollmentConfirmationRequired` ci-dessus (qui compare le MÊME
// `declaredChildId` entre tenants) — les deux 409 peuvent survenir sur cette même
// route, jamais confondus.
export interface EnrollmentDuplicateInTenant {
  error: 'duplicate_student_in_tenant';
  message: string;
  duplicate: {
    existingStudentId: string;
    firstName: string;
    lastName: string;
    currentClassId: string | null;
    currentClassName: string | null;
  };
}

export interface EnrollmentStatusEntry {
  // ENR-07 : exposé pour la première fois — nécessaire pour référencer une
  // inscription précise (POST /enrollments/:id/documents), voir
  // apps/api/src/routes/enrollments.ts.
  id: string;
  // PAY-01 : exposé pour la première fois — TOUJOURS non-nul (Student créé DANS LA
  // MÊME transaction que Enrollment, ENR-01). Permet un lien direct vers
  // GET /parents/students/:studentId/financial-overview sans requête supplémentaire.
  studentId: string;
  declaredChildId: string;
  childFirstName: string;
  childLastName: string;
  schoolId: string;
  schoolName: string;
  requestedLevel: string;
  status: string;
  createdAt: string;
  // ENR-04 : motif du refus — non nul UNIQUEMENT si status === 'REJECTED', visible
  // ici car cette route reste scopée au parent authentifié courant (ses propres
  // enfants déclarés uniquement, voir apps/api/src/routes/enrollments.ts).
  decisionReason: string | null;
  // Maquette "Parent Dashboard.dc.html" — non nul UNIQUEMENT si status ===
  // 'WAITLISTED', même calcul que côté école (ENR-02/ENR-16,
  // computeWaitlistPositions()).
  waitlistPosition: number | null;
}

export interface ListEnrollmentsResponse {
  enrollments: EnrollmentStatusEntry[];
}

export function listEnrollments(client: ApiClient, accessToken: string) {
  return client.get<ListEnrollmentsResponse>('/enrollments', { accessToken });
}

// ─── ENR-02 : file des demandes CÔTÉ ÉCOLE (school admin) ──────────────────────
// Formes alignées sur GET /enrollments/review et POST /enrollments/:id/decision
// (apps/api/src/routes/enrollments.ts) — distinctes du tableau de bord parent
// ci-dessus (chemin dédié, même raison documentée côté backend).

// ENR-06 : WAITLISTED ajouté (liste d'attente si classe pleine).
export type EnrollmentReviewStatus = 'PENDING' | 'WAITLISTED' | 'APPROVED' | 'REJECTED';

// SCH-18 : distingue une demande soumise en ligne (parent) d'une demande créée
// par un import CSV admin (apps/api/src/routes/students.ts) — utilisé
// notamment pour afficher un badge "Importé" et expliquer pourquoi le
// garde-fou documents (ENR-07) ne s'applique pas à ces demandes.
export type EnrollmentSource = 'ONLINE' | 'IMPORT';

// Maquette "Inscriptions.dc.html" — récap minimal de la facture REGISTRATION
// de cette demande (même forme que `selectInvoiceRecap()` côté backend,
// apps/api/src/lib/payment.ts), `null` si pas encore générée (ENR-11, worker
// asynchrone) — voir apps/api/src/lib/enrollmentReview.ts.
export interface EnrollmentReviewRegistrationInvoice {
  id: string;
  // PAY-19 : numéro de facture lisible.
  invoiceNumber: string | null;
  amountDue: string;
  amountPaid: string;
  balance: string;
  dueDate: string;
  status: string;
}

export interface EnrollmentReviewEntry {
  id: string;
  status: EnrollmentReviewStatus;
  requestedLevel: string;
  schoolYear: string;
  source: EnrollmentSource;
  // ENR-03 : NULL tant que non APPROVED — voir apps/api/src/routes/
  // enrollments.ts#POST /enrollments/:id/decision.
  classId: string | null;
  createdAt: string;
  decidedAt: string | null;
  decisionReason: string | null;
  // ENR-30 — `null` tant que l'école n'a pas explicitement pré-validé le
  // contact parental (POST /enrollments/:id/pre-validate) ; requis avant
  // APPROVED, indépendamment du paiement — voir EnrollmentReviewPanel.tsx.
  parentContactPreValidatedAt: string | null;
  // ENR-24 — note interne admin (inscription manuelle unitaire), JAMAIS exposée
  // côté parent — voir apps/api/src/lib/enrollmentReview.ts.
  adminNote: string | null;
  registrationInvoice: EnrollmentReviewRegistrationInvoice | null;
  // Non-nul UNIQUEMENT quand la requête filtrait `status: 'WAITLISTED'` — voir
  // apps/api/src/lib/enrollmentReview.ts#computeWaitlistPositions().
  waitlistPosition: number | null;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    sex: string;
    matricule: string;
    parentContactName: string | null;
    parentContactPhone: string | null;
    // ENR-29 — ajoutés au sérialiseur backend (`lib/enrollmentReview.ts`) pour
    // pré-remplir le formulaire d'édition de `EnrollmentReviewPanel.tsx` sans
    // redécouper `parentContactName` côté frontend.
    parentContactFirstName: string | null;
    parentContactLastName: string | null;
    // ENR-28 — ajouté au sérialiseur backend (`lib/enrollmentReview.ts`), qui
    // manquait ce champ alors qu'il est déjà persisté depuis ENR-24/SCH-21.
    parentContactEmail: string | null;
    // ENR-30 (PR 3/3) — rôle du contact parental, ensemble FERMÉ
    // (mother/father/guardian), édité via le même formulaire ENR-29
    // (`PATCH /students/:id`) — voir EnrollmentReviewPanel.tsx.
    parentContactRelationship: string | null;
  };
  // ⚠️ ENR-28 : représente le compte AYANT SOUMIS la demande (`Enrollment.
  // requestedBy`), PAS forcément le contact parental — pour `source ===
  // 'IMPORT'` (inscription manuelle ENR-24, import CSV SCH-18), c'est le
  // membre du personnel qui a créé la demande (aucun vrai compte Parent
  // n'existe pour un contact en texte libre, Option B). Pour une demande
  // self-service (`source !== 'IMPORT'`), c'est bien le parent authentifié.
  // Ne JAMAIS afficher ce champ comme "le parent" sans vérifier `source` en
  // premier — voir `EnrollmentReviewPanel.tsx`.
  requester: { id: string; fullName: string; email: string; phone: string | null };
}

export interface ListEnrollmentsForReviewParams {
  status?: EnrollmentReviewStatus;
  level?: string;
  q?: string;
  page?: number;
  pageSize?: number;
  // Maquette "Inscriptions.dc.html" — défaut `desc` (comportement historique
  // inchangé) si omis, voir apps/api/src/routes/enrollments.ts.
  sort?: 'asc' | 'desc';
}

export interface ListEnrollmentsForReviewResponse {
  enrollments: EnrollmentReviewEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export function listEnrollmentsForReview(
  client: ApiClient,
  params: ListEnrollmentsForReviewParams,
  accessToken: string,
) {
  return client.get<ListEnrollmentsForReviewResponse>('/enrollments/review', {
    accessToken,
    query: {
      status: params.status,
      level: params.level,
      q: params.q,
      page: params.page?.toString(),
      pageSize: params.pageSize?.toString(),
      sort: params.sort,
    },
  });
}

// NOT-02 — récupère UNE SEULE demande par id (GET /enrollments/:id), MÊME forme
// qu'une ligne de `listEnrollmentsForReview` ci-dessus (`EnrollmentReviewEntry`,
// partagée) — cible du lien direct (`?enrollmentId=`) reçu dans l'email de
// notification "nouvelle demande" envoyé à l'école, voir apps/web-admin/src/
// pages/Enrollments.tsx pour la logique d'ouverture automatique du tiroir.
export interface GetEnrollmentReviewEntryResponse {
  enrollment: EnrollmentReviewEntry;
}

export function getEnrollmentReviewEntry(client: ApiClient, enrollmentId: string, accessToken: string) {
  return client.get<GetEnrollmentReviewEntryResponse>(`/enrollments/${enrollmentId}`, { accessToken });
}

// ENR-29, point 4 — bouton DÉDIÉ EXPLICITE (jamais un envoi automatique) qui
// renvoie la confirmation de prise en compte de l'inscription au contact
// parental d'une demande `IMPORT`, voir apps/api/src/routes/enrollments.ts#POST
// /enrollments/:id/send-confirmation-email.
export interface SendEnrollmentConfirmationEmailResponse {
  status: 'queued';
}

export function sendEnrollmentConfirmationEmail(client: ApiClient, enrollmentId: string, accessToken: string) {
  return client.post<SendEnrollmentConfirmationEmailResponse>(
    `/enrollments/${enrollmentId}/send-confirmation-email`,
    {},
    { accessToken },
  );
}

// ENR-30 — l'école confirme avoir vérifié humainement le contact parental
// (email/téléphone/nom-prénom) d'une demande PENDING, AVANT que la facture
// REGISTRATION ne soit générée et que APPROVED ne devienne possible. Voir
// apps/api/src/routes/enrollments.ts#POST /enrollments/:id/pre-validate.
export interface PreValidateEnrollmentResponse {
  enrollment: {
    id: string;
    parentContactPreValidatedAt: string;
  };
}

export function preValidateEnrollment(client: ApiClient, enrollmentId: string, accessToken: string) {
  return client.post<PreValidateEnrollmentResponse>(`/enrollments/${enrollmentId}/pre-validate`, {}, { accessToken });
}

// ENR-06 : le résultat d'une décision peut désormais valoir CANCELLED (nouvelle
// transition APPROVED -> CANCELLED/REJECTED, voir lib/enrollmentDecision.ts côté
// backend) — DISTINCT de `EnrollmentReviewStatus` (les valeurs filtrables sur GET
// /enrollments/review, qui n'inclut PAS CANCELLED, voir REVIEWABLE_STATUSES).
export type EnrollmentDecisionOutcomeStatus = EnrollmentReviewStatus | 'CANCELLED';

export interface DecideEnrollmentPayload {
  decision: 'APPROVED' | 'REJECTED' | 'CANCELLED';
  // ENR-04 : REQUIS quand decision === 'REJECTED' (400 reason_required côté
  // backend sinon, aucune écriture) — que la transition source soit PENDING ou
  // WAITLISTED (ENR-12). Reste optionnel pour APPROVED/CANCELLED, inchangé.
  reason?: string;
  // ENR-03 : REQUIS quand decision === 'APPROVED' (400 class_id_required côté
  // backend sinon) — ignoré pour REJECTED/CANCELLED. Voir apps/api/src/routes/
  // enrollments.ts pour la revérification de capacité PRÉCISE de cette classe.
  classId?: string;
}

export interface DecideEnrollmentResponse {
  enrollment: {
    id: string;
    status: EnrollmentDecisionOutcomeStatus;
    classId: string | null;
    decidedAt: string | null;
    decisionReason: string | null;
  };
}

export function decideEnrollment(
  client: ApiClient,
  enrollmentId: string,
  payload: DecideEnrollmentPayload,
  accessToken: string,
) {
  return client.post<DecideEnrollmentResponse>(`/enrollments/${enrollmentId}/decision`, payload, { accessToken });
}

// Corps de l'erreur 409 renvoyée par POST /enrollments/:id/decision quand la demande
// a déjà été décidée (transition définitive) — voir `ApiError.body`.
export interface EnrollmentAlreadyDecided {
  error: 'already_decided';
  message: string;
  status: EnrollmentDecisionOutcomeStatus;
}

// ─── ENR-08 : réinscription d'un élève EXISTANT (année suivante) ────────────────
// Formes alignées sur GET /enrollments/reenrollable et POST /enrollments/reenroll
// (apps/api/src/routes/enrollments.ts) — DISTINCT du flux ENR-01 ci-dessus (nouvel
// élève, jamais connu, passe par `DeclaredChild`/`createEnrollment`).

export interface ReenrollableCandidate {
  studentId: string;
  tenantId: string;
  schoolName: string;
  childFirstName: string;
  childLastName: string;
  currentLevel: string;
  // Niveau proposé par défaut — égal à `currentLevel` si `noAutomaticPromotion`
  // (dernière classe du cycle, voir apps/api/src/lib/reenrollment.ts), toujours
  // modifiable avant confirmation.
  proposedLevel: string;
  noAutomaticPromotion: boolean;
  availableLevels: string[];
  activeSchoolYearLabel: string | null;
  openPhase: boolean;
  alreadyReenrolled: boolean;
  // ENR-13 : réinscription déjà INITIÉE (facture REGISTRATION générée) mais pas
  // encore payée — distinct d'`alreadyReenrolled` (APPROVED). Toujours `false`
  // quand `alreadyReenrolled` est `true`.
  pendingPayment: boolean;
}

export interface ListReenrollableResponse {
  candidates: ReenrollableCandidate[];
}

export function listReenrollableStudents(client: ApiClient, accessToken: string) {
  return client.get<ListReenrollableResponse>('/enrollments/reenrollable', { accessToken });
}

export interface ReenrollPayload {
  studentId: string;
  tenantId: string;
  level: string;
}

export interface ReenrollResponse {
  student: { id: string; firstName: string; lastName: string };
  enrollment: { id: string; status: string; requestedLevel: string; schoolYear: string };
  schoolYear: string;
}

export function reenrollStudent(client: ApiClient, payload: ReenrollPayload, accessToken: string) {
  return client.post<ReenrollResponse>('/enrollments/reenroll', payload, { accessToken });
}

// ─── ENR-10 : suivi des réinscriptions CÔTÉ ÉCOLE ────────────────────────────────
// Forme alignée sur GET /enrollments/reenrollment-status (apps/api/src/routes/
// enrollments.ts) — DISTINCT de `listReenrollableStudents` ci-dessus (scopé au
// parent authentifié) : ici, scope tenant entier (SCHOOL_ADMIN_ONLY).

export interface ReenrolledStudent {
  studentId: string;
  firstName: string;
  lastName: string;
  matricule: string;
  level: string;
  // UX-19 : "date de réinscription" (maquette "Reinscriptions.dc.html") — voir
  // apps/api/src/lib/reenrollment.ts#getApprovedEnrollmentForYear() pour le
  // choix de la colonne source (`updatedAt`, pas `decidedAt`).
  reenrolledAt: string;
}

export interface NotYetReenrolledStudent {
  studentId: string;
  firstName: string;
  lastName: string;
  matricule: string;
  level: string;
  schoolYear: string;
  // UX-19 : "nom du parent" (maquette "Reinscriptions.dc.html") — le requérant
  // de la dernière inscription APPROVED connue de l'élève (`User.fullName`,
  // TOUJOURS renseigné), pas `Student.parentContactName` (SCH-12, nullable,
  // presque jamais peuplé pour un élève inscrit en ligne) — voir
  // apps/api/src/lib/reenrollment.ts#getMostRecentApprovedEnrollmentWithRequester().
  parentName: string;
}

export interface ReenrollmentStatusResponse {
  activeSchoolYearLabel: string | null;
  // UX-19 : date d'ouverture de la phase d'inscription ACTUELLEMENT ouverte
  // pour l'année active — `null` si aucune (cas limite documenté, jamais une
  // date inventée côté frontend).
  periodOpenSince: string | null;
  reenrolled: ReenrolledStudent[];
  notYetReenrolled: NotYetReenrolledStudent[];
}

export function getReenrollmentStatus(client: ApiClient, accessToken: string) {
  return client.get<ReenrollmentStatusResponse>('/enrollments/reenrollment-status', { accessToken });
}

// Maquette "Reinscriptions.dc.html" — bouton "Relancer" (individuel ET
// groupé, un seul appel avec `studentIds`) — voir apps/api/src/routes/
// enrollments.ts#POST /enrollments/reenrollment-status/reminders. Envoie
// RÉELLEMENT un email (NOT-01) au parent connu de chaque élève ; `skipped`
// couvre les cas best-effort (aucun email connu, id introuvable, échec de
// publication) — jamais un échec de tout le lot pour un seul élève.
export interface SendReenrollmentRemindersResponse {
  sent: string[];
  skipped: { studentId: string; reason: string }[];
}

export function sendReenrollmentReminders(client: ApiClient, studentIds: string[], accessToken: string) {
  return client.post<SendReenrollmentRemindersResponse>(
    '/enrollments/reenrollment-status/reminders',
    { studentIds },
    { accessToken },
  );
}

// ENR-24 — "+ Inscrire un élève" : inscription manuelle unitaire par un admin,
// voir apps/api/src/routes/enrollments.ts#POST /enrollments/manual. Contact
// parental en TEXTE LIBRE (Option B). ENR-26 : `parentContactPhone` REQUIS ;
// `parentContactEmail` (nouveau) reste optionnel. SCH-43 avait aligné l'ancien
// `CreateStudentPayload`/SCH-40 sur le même téléphone requis — devenu sans
// objet depuis ENR-31 (PR 4/4), qui a retiré `POST /students` et
// `createStudent()` : ce type est désormais le SEUL point d'entrée de
// création manuelle d'un élève.
export interface CreateManualEnrollmentPayload {
  firstName: string;
  lastName: string;
  birthDate: string;
  sex: Sex;
  classId: string;
  parentContactFirstName?: string;
  parentContactLastName?: string;
  parentContactPhone: string;
  parentContactEmail?: string;
  note?: string;
  duplicateResolution?: DuplicateResolution;
  // ENR-27 : assignation PRÉVENTIVE optionnelle d'une réduction (PAY-10) —
  // `discountReason` REQUIS si `discountTypeId` est fourni, voir
  // apps/api/src/routes/enrollments.ts.
  discountTypeId?: string;
  discountReason?: string;
  // ENR-31 (PR 3/3) : `POST /enrollments/manual` a gagné le même avertissement
  // de capacité que l'ancien `POST /students` (SCH-16, retiré en PR 4/4) —
  // renvoyer `true` dans une requête suivante (mêmes champs) procède à
  // l'écriture malgré un avertissement `kind: 'capacity_exceeded'` déjà reçu.
  confirmCapacityOverride?: boolean;
}

// Même discipline que l'ancien `CreateStudentResult` (SCH-06/16, retiré en
// ENR-31 PR 4/4 avec `POST /students`) — un doublon (409) OU un avertissement
// de capacité (409, ENR-31) sont des résultats métier ATTENDUS du round-trip
// de résolution, pas des erreurs techniques à attraper par l'appelant.
export type CreateManualEnrollmentResult =
  | { kind: 'created'; student: Student; enrollmentStatus: 'PENDING' | 'WAITLISTED' }
  | { kind: 'transferred'; student: Student; transferred: boolean }
  | { kind: 'duplicate'; duplicate: ExistingStudentMatch }
  | { kind: 'capacity_exceeded'; capacity: CapacityWarning };

export async function createManualEnrollment(
  client: ApiClient,
  payload: CreateManualEnrollmentPayload,
  accessToken: string,
): Promise<CreateManualEnrollmentResult> {
  const raw = await postAllowingDuplicate<
    | { student: Student; enrollmentStatus: 'PENDING' | 'WAITLISTED' }
    | { student: Student; transferred: boolean }
    | { duplicate: ExistingStudentMatch }
    | { capacity: CapacityWarning }
  >(client, '/enrollments/manual', payload, accessToken);

  if ('duplicate' in raw) {
    return { kind: 'duplicate', duplicate: raw.duplicate };
  }
  if ('capacity' in raw) {
    return { kind: 'capacity_exceeded', capacity: raw.capacity };
  }
  if ('transferred' in raw) {
    return { kind: 'transferred', student: raw.student, transferred: raw.transferred };
  }
  return { kind: 'created', student: raw.student, enrollmentStatus: raw.enrollmentStatus };
}
